// api/dataModeling.ts
/**
 * Client for customer models: Data Management → Models.
 *
 * Backend: apps/backend/api/routes/data_modeling_routes.py
 * BFF:     apps/bff/src/routes/data-modeling.routes.ts
 * Plan:    docs/plans-pending/2026-10-21-customer-data-modeling-platform.md (API, Frontend, P2.6)
 *
 * A project holds models; a model has a draft (body + config, written as you
 * go) and at most one published version. Publishing goes through a change set
 * (`openChangeSet` → `publishChangeSet` with the project's `revision`): a
 * stale revision or a set already in progress is a 409, a model that fails the
 * gates a 422, both with `{message, errors, holder}` as the detail. Every
 * route 404s for an account without the `data_modeling` flag and answers only
 * an account OWNER or ADMIN.
 */

import { ApiError, apiBlob, apiJson } from "./client";

const BASE = "/data-modeling";

export type ModelKind = "FULL" | "VIEW";

export type ModelConfig = {
  kind?: ModelKind | null;
  cron?: string | null;
  columns?: Record<string, string> | null;
  partition_by?: string | null;
  cluster_by?: string[] | null;
  grain?: string[] | null;
  description?: string | null;
};

export type Project = {
  id: string;
  name: string;
  slug: string;
  description: string;
  revision: number;
  repo_full_name: string | null;
  published_change_set_id: string | null;
  promoted_change_set_id: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type ModelStatus = "draft" | "published" | "broken" | "disabled" | string;

export type ModelListItem = {
  id: string;
  name: string;
  slug: string;
  folder_path: string;
  physical_name: string;
  status: ModelStatus;
  kind: string;
  draft_changed: boolean;
};

export type ModelVersion = {
  id: string;
  body: string;
  config: ModelConfig;
  mirrored_sha: string | null;
  created_at: string | null;
};

export type Model = {
  id: string;
  project_id: string;
  name: string;
  slug: string;
  folder_path: string;
  physical_name: string;
  status: ModelStatus;
  kind: string;
  cron: string | null;
  description: string;
  draft_body: string | null;
  draft_config: ModelConfig | null;
  draft_changed: boolean;
  published_version: ModelVersion | null;
  updated_at: string | null;
};

/** One refusal of a gate. Names a gate, a code and the table or column
 *  involved; never a value from the data. */
export type GateError = {
  gate: string;
  code: string;
  message: string;
  subject?: string | null;
};

export type ModelCheck = {
  model_id: string;
  ok: boolean;
  errors: GateError[];
  reads: string[];
  dry_run: Record<string, unknown> | null;
};

export type Preview = {
  ok: boolean;
  errors: GateError[];
  columns: { name: string; type: string }[];
  rows: unknown[][];
  row_limit: number;
  bytes_processed: number | null;
};

export type ChangeSet = {
  id: string;
  project_id: string;
  status: string;
  model_ids: string[];
  rollback_of_change_set_id: string | null;
  validation_report: Record<string, unknown> | null;
  plan_summary: Record<string, unknown> | null;
  dev_environment: string | null;
  failure_reason: string | null;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type PublishResult = {
  change_set: ChangeSet;
  revision: number;
  promote_launched: boolean;
  models: ModelCheck[];
  left_alone: string[];
};

export type ChangeSetCheck = { change_set: ChangeSet; models: ModelCheck[] };

/** One published change set in a project's history, newest first. */
export type HistoryEntry = {
  change_set_id: string;
  published_at: string | null;
  rollback_of_change_set_id: string | null;
  /** The project's published pin points here. */
  is_published: boolean;
  /** Live in the warehouse: a prod run built it. */
  is_promoted: boolean;
  versions: { model_id: string; version_id: string; mirrored_sha: string | null }[];
};

export type CatalogTable = {
  name: string;
  ownership: "model" | "verity" | "customer";
  model_id: string | null;
  project_id: string | null;
  model_status: string | null;
};

export type Catalog = {
  tables: CatalogTable[];
  unavailable: { dataset: string; reason: "not_granted" | "missing" | "unavailable" }[];
  truncated: boolean;
};

export type CatalogColumn = { name: string; type: string; mode: string; fields?: CatalogColumn[] | null };

export type Run = {
  id: string;
  environment: string;
  change_set_id: string | null;
  trigger_type: string;
  status: string;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  revision: string | null;
  applied: boolean;
  model_counts: Record<string, number>;
};

export type RunModel = {
  model_id: string;
  status: string;
  reason: string | null;
  upstream_fallback: boolean;
  name: string | null;
  project_id: string | null;
};

export type RunDetail = Run & { models: RunModel[] };

/** A model of the account, or a table one reads (lineage v1, P2.7):
 *  `verity` a Verity output, `customer` the customer's own table, `missing`
 *  a model name no model has. `pending`: the draft differs from what is live;
 *  `unreadable`: the SQL does not parse, so what it reads is unknown. */
export type LineageNode = {
  id: string;
  kind: "model" | "verity" | "customer" | "missing";
  name: string;
  table: string;
  model_id: string | null;
  project_id: string | null;
  status: string | null;
  pending: boolean;
  unreadable: boolean;
};

export type Lineage = {
  nodes: LineageNode[];
  /** upstream → the model that reads it */
  edges: { source: string; target: string }[];
  truncated: boolean;
};

export type ExportFormat = "sqlmesh_project" | "bigquery_sql";

const json = (body: unknown): RequestInit => ({
  body: JSON.stringify(body),
  headers: { "Content-Type": "application/json" },
});

// --- projects -------------------------------------------------------------------

export const listProjects = () => apiJson<Project[]>(`${BASE}/projects`);

export const createProject = (input: { name: string; slug: string; description?: string }) =>
  apiJson<Project>(`${BASE}/projects`, { method: "POST", ...json(input) });

export const getProject = (projectId: string) => apiJson<Project>(`${BASE}/projects/${projectId}`);

// --- models ---------------------------------------------------------------------

export const listModels = (projectId: string) =>
  apiJson<ModelListItem[]>(`${BASE}/projects/${projectId}/models`);

export const getModel = (projectId: string, modelId: string) =>
  apiJson<Model>(`${BASE}/projects/${projectId}/models/${modelId}`);

export const createModel = (
  projectId: string,
  input: { name: string; slug: string; folder_path?: string; body?: string; config?: ModelConfig },
) => apiJson<Model>(`${BASE}/projects/${projectId}/models`, { method: "POST", ...json(input) });

/** PATCH writes the draft (body, config) and, live, name and folder. */
export const updateModel = (
  projectId: string,
  modelId: string,
  input: { name?: string; folder_path?: string; body?: string; config?: ModelConfig },
) => apiJson<Model>(`${BASE}/projects/${projectId}/models/${modelId}`, { method: "PATCH", ...json(input) });

/** The static gates over the saved draft. */
export const validateModel = (projectId: string, modelId: string) =>
  apiJson<ModelCheck>(`${BASE}/projects/${projectId}/models/${modelId}/validate`, { method: "POST" });

export const setModelDisabled = (projectId: string, modelId: string, disabled: boolean) =>
  apiJson<Model>(`${BASE}/projects/${projectId}/models/${modelId}/disable`, {
    method: "POST",
    ...json({ disabled }),
  });

/** Rows from the text in the editor (not the saved draft), capped. */
export const previewModel = (input: { project_id: string; model_id?: string; body: string }) =>
  apiJson<Preview>(`${BASE}/preview`, { method: "POST", ...json(input) });

// --- change sets ----------------------------------------------------------------

export const openChangeSet = (projectId: string, modelIds: string[]) =>
  apiJson<ChangeSet>(`${BASE}/projects/${projectId}/change-sets`, { method: "POST", ...json({ model_ids: modelIds }) });

export const publishChangeSet = (changeSetId: string, ifMatch: number) =>
  apiJson<PublishResult>(`${BASE}/change-sets/${changeSetId}/publish`, { method: "POST", ...json({ if_match: ifMatch }) });

export const abandonChangeSet = (changeSetId: string) =>
  apiJson<ChangeSet>(`${BASE}/change-sets/${changeSetId}/abandon`, { method: "POST" });

/** Also frees a set whose evaluate run never reported (the server expires it on read). */
export const getChangeSet = (changeSetId: string) => apiJson<ChangeSet>(`${BASE}/change-sets/${changeSetId}`);

/** The static gates over every member's saved draft. */
export const validateChangeSet = (changeSetId: string) =>
  apiJson<ChangeSetCheck>(`${BASE}/change-sets/${changeSetId}/validate`, { method: "POST" });

/** Gates, then a run in a test environment. Asynchronous: poll `getChangeSet`
 *  until the set leaves `evaluating`. */
export const evaluateChangeSet = (changeSetId: string) =>
  apiJson<ChangeSetCheck>(`${BASE}/change-sets/${changeSetId}/evaluate`, { method: "POST" });

export const getHistory = (projectId: string) => apiJson<HistoryEntry[]>(`${BASE}/projects/${projectId}/history`);

/** Publish the project as `changeSetId` published it, through the same gates. */
export const rollbackProject = (projectId: string, changeSetId: string, ifMatch: number) =>
  apiJson<PublishResult>(`${BASE}/projects/${projectId}/rollback`, {
    method: "POST",
    ...json({ change_set_id: changeSetId, if_match: ifMatch }),
  });

// --- catalog, runs, export ------------------------------------------------------

export const getCatalog = (q = "") =>
  apiJson<Catalog>(`${BASE}/catalog${q ? `?q=${encodeURIComponent(q)}` : ""}`);

export const getCatalogColumns = (table: string) =>
  apiJson<{ table: string; columns: CatalogColumn[] }>(`${BASE}/catalog/columns?table=${encodeURIComponent(table)}`);

export const listRuns = (limit = 20) => apiJson<Run[]>(`${BASE}/runs?environment=prod&limit=${limit}`);

export const getRun = (runId: string) => apiJson<RunDetail>(`${BASE}/runs/${runId}`);

/** Ask a running run to stop. It only asks: the run turns Cancelled itself
 *  once it has stopped, so read the run again for its status. */
export const cancelRun = (runId: string) =>
  apiJson<{ id: string; stopping: boolean }>(`${BASE}/runs/${runId}/cancel`, { method: "POST" });

// --- lineage --------------------------------------------------------------------

export const getLineage = () => apiJson<Lineage>(`${BASE}/lineage`);

/** "Run now": the next prod run rebuilds these published models and what reads them. */
export const requestRun = (modelIds: string[]) =>
  apiJson<{ model_ids: string[]; launched: boolean }>(`${BASE}/runs`, { method: "POST", ...json({ model_ids: modelIds }) });

export const exportProject = (projectId: string, format: ExportFormat) =>
  apiBlob(`${BASE}/projects/${projectId}/export?format=${format}`);

// --- refusals -------------------------------------------------------------------

/** What a refused write said: the route's `{message, errors, holder}`, or a
 *  plain `detail` string. `errors` holds one entry per model that failed
 *  (`ModelCheck`-shaped) on a publish, gate errors elsewhere. */
export type Refusal = {
  status: number;
  message: string;
  errors: GateError[];
  holder: string | null;
};

export function refusalOf(err: unknown): Refusal | null {
  if (!(err instanceof ApiError)) return null;
  const detail = err.detail as unknown;
  if (detail && typeof detail === "object" && "message" in detail) {
    const d = detail as { message?: unknown; errors?: unknown; holder?: unknown };
    return {
      status: err.status,
      message: typeof d.message === "string" ? d.message : err.message,
      errors: flattenErrors(d.errors),
      holder: typeof d.holder === "string" ? d.holder : null,
    };
  }
  return {
    status: err.status,
    message: typeof detail === "string" ? detail : err.message,
    errors: [],
    holder: null,
  };
}

/** A publish refusal carries model checks (`{model_id, errors: [...]}`);
 *  other refusals carry gate errors. Both become gate errors. */
function flattenErrors(raw: unknown): GateError[] {
  if (!Array.isArray(raw)) return [];
  const out: GateError[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const e = item as Record<string, unknown>;
    if (Array.isArray(e.errors)) {
      out.push(...flattenErrors(e.errors));
    } else if (typeof e.code === "string" && typeof e.message === "string") {
      out.push({
        gate: typeof e.gate === "string" ? e.gate : "",
        code: e.code,
        message: e.message,
        subject: typeof e.subject === "string" ? e.subject : null,
      });
    }
  }
  return out;
}
